import { ActionIcon, Box, CloseButton, Group, Paper, Text } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { AnnouncementCorner, type PortalAnnouncement } from "@nodus/announcements";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type TouchEvent } from "react";

/** A horizontal swipe shorter than this is a tap or a scroll, not a page turn. */
const SWIPE_PX = 40;

const SLIDE_KEYFRAMES =
  "@keyframes nodus-ann-slide-next{from{opacity:0;transform:translateX(24px)}to{opacity:1;transform:none}}" +
  "@keyframes nodus-ann-slide-prev{from{opacity:0;transform:translateX(-24px)}to{opacity:1;transform:none}}";

interface AnnouncementCornerCarouselProps<A extends PortalAnnouncement> {
  announcements: A[];
  /** Dismiss one card (the host's per-card dismissal: permanent or session). */
  onDismiss: (a: A) => void;
  onOpen?: (a: A, path: string) => void;
  /** The card to start on (tests; the host starts at the first). */
  initialIndex?: number;
}

/**
 * The member portal's corner announcements (events, posters) as ONE popup.
 *
 * Several active corner cards used to stack, and on a phone the stack covered
 * the page until each was dismissed. Now one card shows at a time; with more
 * than one, a strip under it pages between them — swipe on touch, the arrow
 * buttons or ←/→ on desktop — with a "2 / 5" count and a close for the whole
 * popup. With one card it is exactly that card, no strip.
 *
 * Dismissal is the host's: a card's ✕ dismisses that announcement (and the
 * next one slides in); the strip's close dismisses every card in the popup,
 * the same as tapping each ✕. The header bell restores any of them.
 *
 * WAI-ARIA carousel pattern: a ``carousel`` region, each card a ``slide``
 * group labelled "n of N", a polite live region (it never auto-rotates).
 * The slide-in animation is skipped under ``prefers-reduced-motion``.
 */
export function AnnouncementCornerCarousel<A extends PortalAnnouncement>({
  announcements,
  onDismiss,
  onOpen,
  initialIndex = 0,
}: AnnouncementCornerCarouselProps<A>) {
  const [rawIndex, setRawIndex] = useState(initialIndex);
  const [direction, setDirection] = useState<"next" | "prev" | null>(null);
  const reduceMotion = useReducedMotion();
  const slidesId = useId();
  const regionRef = useRef<HTMLElement>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  // A dismissed card takes its focused ✕ with it; focus moves to the card
  // that replaces it rather than dropping to <body>.
  const refocus = useRef(false);

  const count = announcements.length;
  // Derived, not synced: dismissing the card at ``i`` leaves the next one at
  // ``i``; dismissing the last one falls back to the new last.
  const index = Math.min(rawIndex, Math.max(count - 1, 0));
  const current = announcements[index];

  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    const region = regionRef.current;
    const target = region?.querySelector<HTMLElement>('[aria-label="Dismiss announcement"]') ?? region;
    target?.focus();
  }, [current?.id]);

  if (!current) return null;

  const paged = count > 1;
  const go = (step: 1 | -1) => {
    if (!paged) return;
    setDirection(step === 1 ? "next" : "prev");
    setRawIndex((index + step + count) % count);
  };
  const dismissCurrent = () => {
    refocus.current = paged;
    setDirection(null);
    onDismiss(current);
  };
  const dismissAll = () => {
    for (const a of announcements) onDismiss(a);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowRight") go(1);
    else if (e.key === "ArrowLeft") go(-1);
    else return;
    e.preventDefault();
  };
  const onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    const start = touchStart.current;
    const t = e.changedTouches[0];
    touchStart.current = null;
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    // Mostly horizontal, or it was a scroll of the page behind.
    if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(t.clientY - start.y)) return;
    go(dx < 0 ? 1 : -1);
  };

  const animation =
    paged && direction && !reduceMotion ? `nodus-ann-slide-${direction} 180ms ease-out` : undefined;

  return (
    <Box
      component="section"
      ref={regionRef}
      tabIndex={-1}
      aria-roledescription="carousel"
      aria-label="Announcements"
      onKeyDown={paged ? onKeyDown : undefined}
      data-announcement-popup
      style={{ outline: "none" }}
    >
      <Box
        id={slidesId}
        aria-live="polite"
        onTouchStart={paged ? onTouchStart : undefined}
        onTouchEnd={paged ? onTouchEnd : undefined}
        // Vertical scrolling stays the browser's; horizontal pans are ours.
        style={paged ? { touchAction: "pan-y" } : undefined}
      >
        <Box
          key={current.id}
          role="group"
          aria-roledescription="slide"
          aria-label={`${index + 1} of ${count}`}
          style={animation ? { animation } : undefined}
        >
          <AnnouncementCorner
            announcement={current}
            onDismiss={dismissCurrent}
            onOpen={onOpen ? (path) => onOpen(current, path) : undefined}
          />
        </Box>
      </Box>

      {paged && (
        <Paper
          mt={6}
          px={4}
          radius="xl"
          shadow="md"
          withBorder
          maw={360}
          data-announcement-strip
        >
          <Group gap={0} justify="space-between" wrap="nowrap">
            <Group gap={0} wrap="nowrap">
              <ActionIcon
                variant="subtle"
                color="gray"
                size={44}
                radius="xl"
                onClick={() => go(-1)}
                aria-label="Previous announcement"
                aria-controls={slidesId}
              >
                <ChevronLeft size={20} />
              </ActionIcon>
              <Text fz="sm" fw={600} miw={44} ta="center" aria-hidden style={{ whiteSpace: "nowrap" }}>
                {index + 1} / {count}
              </Text>
              <ActionIcon
                variant="subtle"
                color="gray"
                size={44}
                radius="xl"
                onClick={() => go(1)}
                aria-label="Next announcement"
                aria-controls={slidesId}
              >
                <ChevronRight size={20} />
              </ActionIcon>
            </Group>
            <CloseButton
              size={44}
              iconSize={18}
              radius="xl"
              onClick={dismissAll}
              aria-label={`Dismiss all ${count} announcements`}
              title="Dismiss all"
            />
          </Group>
        </Paper>
      )}
      {paged && !reduceMotion && <style>{SLIDE_KEYFRAMES}</style>}
    </Box>
  );
}
